import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Container, Typography, Button, Grid, Card, CardActionArea, CardContent } from '@mui/material';
import ArtistHero from '@/components/ArtistPage/ArtistHero';
import ArtistInfo from '@/components/ArtistPage/ArtistInfo';
import ArtistAlbums from '@/components/ArtistPage/ArtistAlbums';
import { getAlbumsByArtistId, fetchArtistPinnedMixtapesBySlug, PublicPinnedMixtape } from '@/services/artistService';
import { getTracksByArtistId } from '@/services/trackService';
import { useAudio } from '@/context/AudioProvider';
import { useAuth } from '@/context/AuthProvider';
import { Album, Artist, Track } from '@/types';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { PLACEHOLDER_ALBUM } from '@/services/mediaService';
import MetaTagManager, { createArtistMetaData } from '@/components/MetaTagManager';
import { API_BASE_URL } from '@/config';
import { getCoverIconDisplay } from '@/utils/coverIcon';

const ArtistPageBySlug: React.FC = () => {
  const { artistIdentifier: slug } = useParams<{ artistIdentifier: string }>();
  const navigate = useNavigate();
  const { user, token, hasActiveSubscription } = useAuth();
  const { playTrack } = useAudio();
  const location = useLocation();

  const [artist, setArtist] = useState<Artist | null>(null);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [firstPlayableAlbum, setFirstPlayableAlbum] = useState<Album | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pinnedMixtapes, setPinnedMixtapes] = useState<PublicPinnedMixtape[]>([]);
  const [pinnedLoading, setPinnedLoading] = useState(true);

  // Refs to albums and tracks for scrolling
  const albumRefs = useRef<Record<string, HTMLElement | null>>({});
  const trackRefs = useRef<Record<string, HTMLElement | null>>({});

  const renderPinnedCover = useCallback((mix?: PublicPinnedMixtape["mixtape"]) => {
    if (mix?.artwork_url) {
      return (
        <Box
          component="img"
          src={mix.artwork_url}
          alt={mix.title}
          sx={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover" }}
        />
      );
    }

    const cfg = mix?.cover_config || {};
    const color = cfg.color || "#111827";
    const label = cfg.label || mix?.title || "Mixtape";
    const icon = cfg.icon || "🎧";
    const font = cfg.font || "Inter";
    const iconDisplay = getCoverIconDisplay(icon);

    return (
      <Box
        sx={{
          width: "100%",
          aspectRatio: "1 / 1",
          bgcolor: color,
          color: "white",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          fontFamily: `"${font}", sans-serif`,
          borderRadius: 1,
          textAlign: "center",
        }}
      >
        {iconDisplay.type === "image" ? (
          <Box
            component="img"
            src={iconDisplay.value}
            alt="Mixtape icon"
            sx={{ width: 56, height: 56 }}
          />
        ) : (
          <Typography variant="h4">{iconDisplay.value}</Typography>
        )}
        <Typography variant="subtitle2" fontWeight={600}>
          {label}
        </Typography>
      </Box>
    );
  }, []);

  useEffect(() => {
    if (!slug) return;

    const loadArtistBySlug = async () => {
      try {
        console.log(`🎵 Loading artist by slug: ${slug}`);
        console.log(`🔗 API_BASE_URL: ${API_BASE_URL}`);
        
        // Try direct slug endpoint first
        const slugUrl = `${API_BASE_URL}/public/artist/slug/${slug}`;
        console.log(`🚀 Trying direct slug endpoint: ${slugUrl}`);
        let res = await fetch(slugUrl);
        console.log(`📡 Direct slug response status: ${res.status}`);
        
        if (!res.ok) {
          console.log(`❌ Direct slug endpoint failed, trying search approach`);
          // Fallback to search if direct slug fails
          const searchUrl = `${API_BASE_URL}/public/artist?q=${encodeURIComponent(slug)}`;
          console.log(`🔍 Trying search endpoint: ${searchUrl}`);
          const searchRes = await fetch(searchUrl);
          console.log(`📡 Search response status: ${searchRes.status}`);
          
          if (!searchRes.ok) throw new Error('Artist not found');
          
          const searchData = await searchRes.json();
          console.log(`🔍 Search data:`, searchData);
          
          // Find best match from search results
          let bestMatch = null;
          if (searchData.artists && searchData.artists.length > 0) {
            // Try to find an exact match first (comparing normalized names)
            const normalizedSlug = slug.toLowerCase();
            console.log(`🔍 Looking for normalized slug: ${normalizedSlug}`);
            
            for (const artist of searchData.artists) {
              console.log(`🔍 Checking artist: ${artist.name}, slug: ${artist.slug}`);
              // Check if the artist has a slug that matches exactly
              if (artist.slug && artist.slug.toLowerCase() === normalizedSlug) {
                bestMatch = artist;
                console.log(`✅ Found exact slug match: ${artist.name}`);
                break;
              }
              
              // Check if the normalized name matches our slug
              const artistNameSlug = artist.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
              console.log(`🔍 Normalized name slug: ${artistNameSlug}`);
              if (artistNameSlug === normalizedSlug) {
                bestMatch = artist;
                console.log(`✅ Found name-based match: ${artist.name}`);
                break;
              }
            }
            
            // If no exact match, just use the first result
            if (!bestMatch) {
              bestMatch = searchData.artists[0];
              console.log(`⚠️ Using first result as fallback: ${bestMatch.name}`);
            }
            
            // Redirect to the correct slug if we found one
            if (bestMatch && bestMatch.slug && bestMatch.slug !== slug) {
              console.log(`🔄 Redirecting to correct slug: ${bestMatch.slug}`);
              navigate(`/artist/${bestMatch.slug}${location.hash}`, { replace: true });
              return;
            }
          }
          
          if (!bestMatch) throw new Error('Artist not found');
          
          // Use the best match artist ID to get full data
          const artistUrl = `${API_BASE_URL}/public/artist/${bestMatch.id}`;
          console.log(`🚀 Fetching artist by ID: ${artistUrl}`);
          res = await fetch(artistUrl);
          console.log(`📡 Artist by ID response status: ${res.status}`);
          if (!res.ok) throw new Error('Artist not found');
        }
        
        const artistData = await res.json();
        console.log(`✅ Artist data loaded:`, artistData);
        console.log(`📀 Albums included: ${artistData.albums ? artistData.albums.length : 0}`);

        // If the API response includes albums, use them directly
        if (artistData.albums && artistData.albums.length > 0) {
          console.log(`🎵 Processing ${artistData.albums.length} albums from API response`);
          
          // Fetch top tracks for the artist (bundle endpoint returns top tracks, not all)
          // For full album tracks, we'll fetch them per-album when needed
          console.log(`🎵 Fetching top tracks for artist ${artistData.id}`);
          const tracksData = await getTracksByArtistId(artistData.id, 100);
          console.log(`✅ Fetched ${tracksData.length} top tracks for artist`);
          
          // Map albums - tracks will be fetched per-album when needed via bundle endpoint
          // For now, we'll use top tracks that match each album
          const albumsWithTracks = artistData.albums.map((album: any) => {
            const albumIdStr = album.id?.toString();
            const albumTracks = tracksData
              .filter(track => {
                // Bundle tracks have album_id in the album object or directly
                const trackAlbumId = track.album_id || track.album?.id;
                return trackAlbumId?.toString() === albumIdStr;
              })
              .sort((a, b) => (a.track_number ?? 0) - (b.track_number ?? 0))
              .map((track): Track => ({
                id: (track.id || track.track_id || track.id)?.toString() || '',
                title: track.title,
                duration: track.duration ?? undefined,
                track_number: track.track_number ?? undefined,
                file_path: track.file_path,
                // Bundle tracks have signed_url for playback, but may also have preview_url
                preview_url: track.signed_url || track.preview_url || null,
                // Preserve access control fields for UI display and playback blocking
                access_type: track.access_type,
                access_granted: track.access_granted,
                access_reason: track.access_reason,
              }));

            console.log(`🎵 Album "${album.title}" (${albumIdStr}) has ${albumTracks.length} top tracks`);
            return {
              ...album,
              tracks: albumTracks.length > 0 ? albumTracks : undefined,
            };
          });
          
          console.log(`✅ Setting artist and ${albumsWithTracks.length} albums`);
          setArtist(artistData);
          setAlbums(albumsWithTracks);
        } else {
          console.log(`🎵 No albums in API response, fetching separately`);
          // Fallback to separate API calls if albums not included
          const albumsData = await getAlbumsByArtistId(artistData.id);
          const tracksData = await getTracksByArtistId(artistData.id, 100);
          console.log(`🎵 Fetched ${albumsData.length} albums and ${tracksData.length} tracks separately`);
          console.log(`🔍 DEBUG (fallback): Album IDs:`, albumsData.map((a: any) => ({ id: a.id, type: typeof a.id, title: a.title })));
          console.log(`🔍 DEBUG (fallback): Track album_ids:`, tracksData.map(t => ({ 
            id: t.id, 
            title: t.title, 
            album_id: t.album_id, 
            album_id_type: typeof t.album_id 
          })));

          const albumsWithTracks = albumsData.map(album => {
            // FIX: Ensure both IDs are strings for comparison
            const albumIdStr = album.id?.toString();
            const albumTracks = tracksData
              .filter(track => {
                // Bundle tracks have album_id in the album object or directly
                const trackAlbumId = track.album_id || track.album?.id;
                return trackAlbumId?.toString() === albumIdStr;
              })
              .sort((a, b) => (a.track_number ?? 0) - (b.track_number ?? 0))
              .map((track): Track => {
                // Bundle tracks have signed_url for playback
                return {
                  id: (track.id || track.track_id)?.toString() || '',
                  title: track.title,
                  duration: track.duration ?? undefined,
                  track_number: track.track_number ?? undefined,
                  file_path: track.file_path,
                  preview_url: track.signed_url || track.preview_url || null,
                  // Preserve access control fields for UI display and playback blocking
                  access_type: track.access_type,
                  access_granted: track.access_granted,
                  access_reason: track.access_reason,
                };
              });

            console.log(`🎵 (fallback) Album "${album.title}" has ${albumTracks.length} tracks`);
            return {
              ...album,
              tracks: albumTracks.length > 0 ? albumTracks : undefined,
            };
          });

          console.log(`✅ Setting artist and ${albumsWithTracks.length} albums (separate fetch)`);
          setArtist(artistData);
          setAlbums(albumsWithTracks);
        }
      } catch (err) {
        console.error('❌ Error loading artist by slug:', err);
        setError('Failed to load artist information');
      } finally {
        console.log(`🏁 Setting loading to false`);
        setLoading(false);
      }
    };

    console.log(`🎬 Starting artist load for slug: ${slug}`);
    setLoading(true);
    setError(null);
    loadArtistBySlug();
  }, [slug, navigate, location.hash]);

  useEffect(() => {
    if (!slug) return;
    let active = true;
    const loadPinned = async () => {
      setPinnedLoading(true);
      try {
        const pins = await fetchArtistPinnedMixtapesBySlug(slug);
        if (!active) return;
        setPinnedMixtapes(pins.filter((pin) => pin.mixtape && pin.mixtape.id));
      } catch {
        if (!active) return;
        setPinnedMixtapes([]);
      } finally {
        if (active) setPinnedLoading(false);
      }
    };
    loadPinned();
    return () => {
      active = false;
    };
  }, [slug]);

  // Handle hash navigation
  useEffect(() => {
    if (loading || !location.hash) return;

    // Handle after a short delay to ensure components are rendered
    const timer = setTimeout(() => {
      const hash = location.hash.substring(1); // Remove the # character
      
      if (hash.startsWith('album-')) {
        const albumId = hash.replace('album-', '');
        const element = albumRefs.current[albumId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Add a highlight effect to the element
          element.classList.add('highlight-item');
          setTimeout(() => element.classList.remove('highlight-item'), 2000);
        }
      } else if (hash.startsWith('track-')) {
        const trackId = hash.replace('track-', '');
        const element = trackRefs.current[trackId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a highlight effect to the element
          element.classList.add('highlight-item');
          setTimeout(() => element.classList.remove('highlight-item'), 2000);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [loading, location.hash, albums]);

  const handlePlayAlbum = useCallback((album: Album) => {
    if (!artist || !album?.tracks || album.tracks.length === 0) return;

    const sortedTracks = [...album.tracks].sort((a, b) => (a.track_number ?? 0) - (b.track_number ?? 0));
    const firstTrack = sortedTracks[0];

    if (!firstTrack) return;

    // Check track-level access control (from bundle endpoint)
    const trackAccessType = (firstTrack as any).access_type || album.access_type || 'standard';
    const trackAccessGranted = (firstTrack as any).access_granted === true;
    
    // For standard tracks, require active subscription
    if (trackAccessType === 'standard') {
      if (!hasActiveSubscription) {
        alert('Active subscription required to play this track.');
        navigate('/subscribe');
        return;
      }
      // Even with subscription, if API explicitly denied access, block playback
      if ((firstTrack as any).access_granted === false) {
        alert((firstTrack as any).access_reason || 'Access denied');
        return;
      }
    }
    
    // For premium tracks (soundcheck/vip/limited), require explicit access_granted === true
    if (trackAccessType === 'soundcheck' || trackAccessType === 'vip' || trackAccessType === 'limited') {
      if (!trackAccessGranted) {
        alert('This track requires purchase. Please visit the album page to purchase access.');
        navigate(`/album/${album.id}`);
        return;
      }
    }
    
    // For free tracks, allow playback (access_granted should be true)
    if (trackAccessType === 'free' && !trackAccessGranted) {
      alert((firstTrack as any).access_reason || 'Access denied');
      return;
    }

    const enrich = (track: Track) => ({
      ...track,
      albumArtwork: album.artwork_url || PLACEHOLDER_ALBUM,
      albumName: album.title,
      artistName: artist.name,
      artistProfileImage: artist.image_url,
    });

    playTrack(enrich(firstTrack), sortedTracks.map(enrich));
  }, [artist, playTrack, navigate]);

  const handleErrorLogging = (error: Error) => {
    console.error("ArtistAlbums component error:", error);
  };

  const goBack = () => navigate(-1);

  // Pass the refs to child components
  const registerAlbumRef = (id: string, element: HTMLElement | null) => {
    albumRefs.current[id] = element;
  };

  const registerTrackRef = (id: string, element: HTMLElement | null) => {
    trackRefs.current[id] = element;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !artist) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="60vh"
          textAlign="center"
        >
          <Typography variant="h5" color="error" gutterBottom>
            {error || "Artist not found"}
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="contained"
            onClick={goBack}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <MetaTagManager data={createArtistMetaData(artist)}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <ArtistHero
          artist={artist}
          firstPlayableAlbum={firstPlayableAlbum}
          onPlayAlbum={handlePlayAlbum}
        />
        <ArtistInfo artist={artist} />
        {pinnedLoading ? null : pinnedMixtapes.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              Featured Mixtapes
            </Typography>
            <Grid container spacing={2}>
              {pinnedMixtapes.map((pin) => (
                <Grid item xs={12} sm={6} md={3} key={pin.id}>
                  <Card>
                    <CardActionArea
                      onClick={() => pin.mixtape?.id && navigate(`/mixtape/${pin.mixtape.id}`)}
                    >
                      {renderPinnedCover(pin.mixtape)}
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {pin.mixtape?.title || "Mixtape"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          Followers: {pin.mixtape?.follower_count?.toLocaleString() ?? 0}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        <ErrorBoundary
          onError={handleErrorLogging}
          resetKey={slug}
          fallback={
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" gutterBottom>
                Releases
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unable to display releases at this time.
              </Typography>
            </Box>
          }
        >
          <ArtistAlbums
            albums={albums}
            artist={artist}
            onAlbumsLoaded={setFirstPlayableAlbum}
            registerAlbumRef={registerAlbumRef}
            registerTrackRef={registerTrackRef}
          />
        </ErrorBoundary>
      </Container>
    </MetaTagManager>
  );
};

export default ArtistPageBySlug;
